import React, { useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../api/client'
import { usePreferences } from '../context/usePreferences'
import PageShell from '../components/ui/PageShell'
import Card from '../components/ui/Card'
import { PrimaryButton, SecondaryButton } from '../components/ui/Button'
import ProgressBar from '../components/ProgressBar'
import './Upload.css'

const ALLOWED = ['.wav', '.mp3', '.flac']
const MAX_FILES = 500
const MAX_BATCH_GB = 40

function allowedFile(file) {
  const name = (file.name || '').toLowerCase()
  return ALLOWED.some((ext) => name.endsWith(ext))
}

function formatSize(bytes) {
  if (bytes == null || bytes === 0) return '–'
  const mb = bytes / (1024 * 1024)
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`
}

const PROFILES = [
  { value: 'spotify_safe', label: 'Spotify Safe (-14 / -1.0)', title: 'Streaming-Baseline, transparent' },
  { value: 'spotify_optimized', label: 'Spotify Optimized (-10 / -1.0)', title: 'Lauter, modern' },
  { value: 'radio', label: 'Radio (-11 / -1.2)', title: 'Broadcast-freundlich' },
  { value: 'club_modern', label: 'Club Modern (-9 / -1.0)', title: 'DJ-freundlich, ausgewogen' },
  { value: 'club_punch', label: 'Club Punch (-8 / -0.6) ★', title: 'Laut, mit Punch – empfohlen für DJs' },
  { value: 'dj_set', label: 'DJ Set (-9.5 / -0.8)', title: 'Long-Play-Konsistenz' },
  { value: 'archive', label: 'Archive (-16 / -1.0)', title: 'Max. Qualität, konservativ' },
  { value: 'club', label: 'Club (Legacy)', title: 'Legacy (may sound flat)' },
  { value: 'tidal_hifi', label: 'Tidal HiFi (-14 / -1.0)', title: 'Hi-Fi Streaming' },
  { value: 'apple_music', label: 'Apple Music (-16 / -1.0)', title: 'Konservativ, dynamisch' },
  { value: 'youtube', label: 'YouTube (-14 / -1.0)', title: 'Video-optimiert' },
]

export default function Upload() {
  const prefs = usePreferences()
  const [drag, setDrag] = useState(false)
  const [files, setFiles] = useState([])
  const [uploading, setUploading] = useState(false)
  const [uploadStatus, setUploadStatus] = useState('') // 'uploading' | 'analyzing' | 'mastering'
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState('')
  const [targetProfile, setTargetProfile] = useState('spotify_safe')
  const [aiCleanupEnabled, setAiCleanupEnabled] = useState(true)
  const [aiCleanupStrength, setAiCleanupStrength] = useState('medium')
  const [thoroughMastering, setThoroughMastering] = useState(false)
  const [normalizationEnabled, setNormalizationEnabled] = useState(true)
  const navigate = useNavigate()
  const fileInputRef = useRef(null)

  const openFileDialog = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const onDrop = useCallback((e) => {
    e.preventDefault()
    setDrag(false)
    const list = Array.from(e.dataTransfer.files).filter(allowedFile)
    setFiles((prev) => {
      const next = [...prev, ...list]
      if (next.length > MAX_FILES) {
        setError(`Maximal ${MAX_FILES} Dateien (insgesamt max. ${MAX_BATCH_GB} GB). Es werden nur die ersten ${MAX_FILES} übernommen.`)
        return next.slice(0, MAX_FILES)
      }
      setError('')
      return next
    })
  }, [])

  const onDragOver = useCallback((e) => {
    e.preventDefault()
    setDrag(true)
  }, [])

  const onDragLeave = useCallback((e) => {
    e.preventDefault()
    setDrag(false)
  }, [])

  const onSelect = (e) => {
    const list = Array.from(e.target.files || []).filter(allowedFile)
    setFiles((prev) => {
      const next = [...prev, ...list]
      if (next.length > MAX_FILES) {
        setError(`Maximal ${MAX_FILES} Dateien (insgesamt max. ${MAX_BATCH_GB} GB). Es werden nur die ersten ${MAX_FILES} übernommen.`)
        return next.slice(0, MAX_FILES)
      }
      setError('')
      return next
    })
    if (e.target) e.target.value = ''
  }

  const remove = (i) => {
    setFiles((prev) => prev.filter((_, idx) => idx !== i))
  }

  const submit = async () => {
    if (files.length === 0) {
      setError('Bitte mindestens eine Datei auswählen (WAV, MP3, FLAC).')
      return
    }
    setUploading(true)
    setError('')
    setUploadStatus('uploading')
    setProgress(0)
    const form = new FormData()
    files.forEach((f) => form.append('files', f))
    form.append('target_profile', targetProfile)
    form.append('ai_cleanup_enabled', aiCleanupEnabled ? '1' : '0')
    form.append('ai_cleanup_strength', aiCleanupStrength)
    form.append('thorough_mastering', thoroughMastering ? '1' : '0')
    form.append('normalization_enabled', normalizationEnabled ? '1' : '0')
    if (prefs.language) {
      form.append('language', prefs.language)
    }
    let didRedirect = false
    try {
      const result = await api.uploadWithProgress('/upload', form, (percent) => {
        setProgress(Math.round((percent / 100) * 90))
      })
      // Erfolg = API 200/201 (Job angelegt). Weiterleitung sofort, nicht warten auf Worker-Ende.
      const data = result?.data ?? result ?? {}
      setUploadStatus('mastering')
      setProgress(100)
      const tracks = Array.isArray(data?.tracks) ? data.tracks : []
      const firstTrackId = tracks[0]?.id ?? data?.track_id ?? data?.id ?? null
      const jobCreated =
        result != null &&
        ((typeof data?.uploaded === 'number' && data.uploaded > 0) ||
          tracks.length > 0 ||
          data?.track_id != null ||
          data?.job_id != null ||
          data?.accepted === true)
      if (jobCreated) {
        setFiles([])
        setError('')
        didRedirect = true
        const target = firstTrackId != null ? `/dashboard?track=${firstTrackId}` : '/dashboard'
        navigate(target, { replace: true, state: { highlightTrackId: firstTrackId ?? undefined } })
        return
      }
    } catch (err) {
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('[Upload] API error', { message: err?.message, name: err?.name })
      }
      const msg = err?.message || ''
      if (msg.includes('credits') || msg.includes('Credits')) {
        setError(msg + ' Siehe Dashboard für Kontostand.')
      } else if (msg === 'Upload fehlgeschlagen' || msg === 'Request aborted' || /network|timeout|failed/i.test(msg)) {
        setError('Upload fehlgeschlagen. Bei großer Datei oder langsamer Verbindung: Bitte Dashboard prüfen – der Upload kann trotzdem durchgelaufen sein.')
      } else {
        setError(msg || 'Upload fehlgeschlagen')
      }
    } finally {
      if (!didRedirect) {
        setUploading(false)
        setProgress(0)
        setUploadStatus('')
      }
    }
  }

  return (
    <PageShell>
      <div className="upload-page mx-auto max-w-2xl space-y-6">
        <Card className="border-white/15 bg-gradient-to-b from-white/10 to-white/5">
          <h2 className="text-xl font-bold text-white">Track hochladen</h2>
          <p className="mt-1 text-sm text-white/60">
            WAV, MP3 oder FLAC. Analyse und Mastering nach Streaming-Standards (LUFS, True Peak).
          </p>

          {uploading && (
            <div className="mt-6 space-y-2">
              <div className="flex justify-between text-sm text-white/70">
                <span>
                  {uploadStatus === 'uploading' && 'Hochladen…'}
                  {uploadStatus === 'analyzing' && 'Analyse…'}
                  {uploadStatus === 'mastering' && 'Weiterleitung…'}
                </span>
                <span className="tabular-nums font-medium text-violet-300">{progress}%</span>
              </div>
              <ProgressBar progress={progress} variant="default" showLabel={false} />
            </div>
          )}

          {!uploading && (
            <>
              <input
                id="upload-file-input"
                ref={fileInputRef}
                type="file"
                multiple
                accept=".wav,.mp3,.flac,audio/wav,audio/mpeg,audio/flac"
                onChange={onSelect}
                className="sr-only"
                aria-hidden="true"
                tabIndex={-1}
              />
              <label
                htmlFor="upload-file-input"
                className={`mt-6 block rounded-xl border-2 border-dashed p-8 text-center transition-all cursor-pointer select-none ${
                  drag
                    ? 'border-violet-400/60 bg-violet-500/10'
                    : 'border-white/20 hover:border-white/40 hover:bg-white/5'
                }`}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(e) }}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(e) }}
                onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); onDragLeave(e) }}
              >
                <span className="mb-2 block text-4xl opacity-70">↑</span>
                <span className="block text-white/80 mb-2">
                  Datei hierher ziehen oder hier klicken zum Auswählen
                </span>
                <span className="inline-block rounded-xl bg-violet-500/30 hover:bg-violet-500/50 px-5 py-2.5 text-sm font-medium text-white border border-violet-400/40 mt-2 pointer-events-none">
                  Datei auswählen
                </span>
                <p className="mt-3 text-xs text-white/50">Max. {MAX_FILES} Dateien, insgesamt max. {MAX_BATCH_GB} GB</p>
              </label>

              {files.length > 0 && (
                <>
                  <div className="mt-6 space-y-3">
                    {files.map((f, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 p-3"
                      >
                        <div className="h-10 w-10 shrink-0 rounded-lg bg-white/10 flex items-center justify-center text-white/60 text-lg">
                          ♪
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-white">{f.name}</p>
                          <p className="text-xs text-white/50">{formatSize(f.size)}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => remove(i)}
                          className="shrink-0 rounded-lg px-3 py-1.5 text-xs text-white/70 hover:bg-red-500/20 hover:text-red-300"
                        >
                          Entfernen
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6">
                    <label className="block text-sm font-medium text-white/80">
                      Mastering-Profil
                    </label>
                    <p className="text-xs text-white/50">Optimiert für Streaming-Plattformen</p>
                    <select
                      value={targetProfile}
                      onChange={(e) => setTargetProfile(e.target.value)}
                      className="mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-white focus:border-violet-500/50 focus:outline-none focus:ring-1 focus:ring-violet-500/50"
                      title={PROFILES.find((p) => p.value === targetProfile)?.title}
                    >
                      {PROFILES.map((p) => (
                        <option key={p.value} value={p.value} title={p.title}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                    {['club_loud', 'club_punch', 'club_modern'].includes(targetProfile) && (
                      <p className="mt-1 text-xs text-white/50">Hinweis: Spotify normalisiert auf ca. -14 LUFS; dein gewähltes Ziel bleibt unverändert.</p>
                    )}
                  </div>

                  <div className="mt-4 space-y-3 rounded-xl border border-white/10 bg-white/5 p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <label className="block text-sm font-medium text-white/80">AI Cleanup</label>
                        <p className="text-xs text-white/50">Reduziert KI-Artefakte und Rauschen vor dem Mastering</p>
                      </div>
                      <label className="flex shrink-0 cursor-pointer items-center gap-2">
                        <input
                          type="checkbox"
                          checked={aiCleanupEnabled}
                          onChange={(e) => setAiCleanupEnabled(e.target.checked)}
                          className="rounded"
                        />
                        <span className="text-sm text-white/80">Aktiv</span>
                      </label>
                    </div>
                    {aiCleanupEnabled && (
                      <div>
                        <label className="block text-xs text-white/50">Stärke</label>
                        <select
                          value={aiCleanupStrength}
                          onChange={(e) => setAiCleanupStrength(e.target.value)}
                          className="mt-1 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white"
                        >
                          <option value="low">Niedrig</option>
                          <option value="medium">Mittel</option>
                          <option value="high">Hoch</option>
                        </select>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 space-y-3 rounded-xl border border-white/10 bg-white/5 p-4">
                    <label className="flex cursor-pointer items-start gap-3">
                      <input
                        type="checkbox"
                        checked={normalizationEnabled}
                        onChange={(e) => setNormalizationEnabled(e.target.checked)}
                        className="mt-1 rounded"
                      />
                      <div>
                        <span className="block text-sm font-medium text-white/80">Lautheits-Normalisierung (LUFS-Ziel)</span>
                        <span className="block text-xs text-white/50">An: Ziel-LUFS gemäß Profil (Streaming-Standard). Aus: Relativer Pegel bleibt, nur True-Peak-Begrenzung.</span>
                      </div>
                    </label>
                    <label className="flex cursor-pointer items-start gap-3">
                      <input
                        type="checkbox"
                        checked={thoroughMastering}
                        onChange={(e) => setThoroughMastering(e.target.checked)}
                        className="mt-1 rounded"
                      />
                      <div>
                        <span className="block text-sm font-medium text-white/80">Gründlicher Durchlauf</span>
                        <span className="block text-xs text-white/50">Mehr Headroom (2 dB unter Ziel), weniger Limiter – etwas bessere Qualität, ähnliche Dauer.</span>
                      </div>
                    </label>
                  </div>

                  {error && (
                    <div className="mt-4 space-y-2">
                      <div className="rounded-xl bg-red-500/10 px-4 py-3 text-sm text-red-200">
                        {error}
                      </div>
                      {error.includes('Dashboard prüfen') && (
                        <SecondaryButton className="w-full" onClick={() => navigate('/dashboard')}>
                          Zum Dashboard
                        </SecondaryButton>
                      )}
                    </div>
                  )}

                  <PrimaryButton
                    className="mt-6 w-full py-3 text-base"
                    onClick={submit}
                    disabled={files.length === 0}
                  >
                    Start AI Mastering
                  </PrimaryButton>
                </>
              )}
            </>
          )}
        </Card>
      </div>
    </PageShell>
  )
}
